import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import AiAutomationRulesSvg from 'ext-ui/svg/illustration/AiAutomationRulesSvg'
import React from 'react'
import { useSelector } from 'react-redux'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import SvgArrowForward from 'src/components/Svgs/SvgArrowForward'
import { ImageNames } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import { connectedPush } from 'src/navigation/Lnk'
import {
  createAutomationRuleUrl,
  expenseCategoriesUrl,
} from 'src/navigation/urls'
import { useQueryExpenseCategories } from 'src/queries/organizations/expenseCategories.queries'
import { selectOrganizationIdFromRoute } from 'src/redux/selectors/data/organizations.selectors'
import styled, { css } from 'styled-components'

const Message = styled.div`
  ${getFontCss(
    16,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )};
`

const AutomationRulesListEmptyState: React.FC = (): React.ReactElement => {
  const dispatch = useBackgroundDispatch()
  const orgId = useSelector(selectOrganizationIdFromRoute)
  const { data, isFetched } = useQueryExpenseCategories({ filters: {} })
  const showNoExpCatsEmptyState = isFetched && data?.length === 0

  const listSearchImage = useColorOrGrayscaleImage(ImageNames.ListSearch)

  return (
    <div
      css={css`
        width: 600px;
        display: flex;
        flex-direction: column;
        align-self: center;
        align-items: center;
        text-align: center;
        gap: 20px;
        ${getBreakpointDown(breakpointSizes.XSM)(css`
          width: 560px;
        `)}
        ${getBreakpointDown(breakpointSizes.XXSM)(css`
          width: 100%;
          padding: 0 20px;
        `)}
      `}
      data-testid={'emptyAutomationRulesList'}
    >
      {showNoExpCatsEmptyState ? (
        <img
          src={listSearchImage}
          alt={''}
          css={css`
            width: 100px;
          `}
        />
      ) : (
        <AiAutomationRulesSvg />
      )}
      <div
        css={css`
          max-width: 50%;
          ${getFontCss(
            20,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )}
          text-align: center;
        `}
      >
        <Trns
          source={
            showNoExpCatsEmptyState
              ? "Looks like you don't have any expense categories"
              : 'Set Up Accounting Automation'
          }
        />
      </div>
      <Message>
        <Trns
          source={
            showNoExpCatsEmptyState
              ? "Make reconciliation a breeze by creating rules to automatically tag transactions. To take advantage of accounting automation you'll first need to create Expense Categories. Click the link below to get started."
              : 'Save time and make reconciliation a breeze by creating rules to automatically tag transactions with expense categories.'
          }
        />
      </Message>
      {!showNoExpCatsEmptyState && (
        <Message>
          <Trns
            source={
              'To get started, click the button below to create your first expense category rule.'
            }
          />
        </Message>
      )}
      {!showNoExpCatsEmptyState && (
        <Message>
          <Trns
            source={
              'Learn more about Accounting Automation in our ${resourceCenter}.'
            }
            expressions={{
              resourceCenter: (
                <ResourceCenterLink
                  underline
                  regular
                  size={HoverableLinkSizes.Large}
                />
              ),
            }}
          />
        </Message>
      )}
      {showNoExpCatsEmptyState ? (
        <HoverableLink
          to={expenseCategoriesUrl(orgId)}
          ariaLabelSource={'Go to Expense Categories'}
          testId={'expCatsBtn'}
        >
          <Trns source={'Go to Expense Categories'} />
          <SvgArrowForward
            css={css`
              transform: scale(calc(18 / 24));
            `}
          />
        </HoverableLink>
      ) : (
        <Button
          testId={'newAutomationRuleBtn'}
          type={ButtonTypes.button}
          design={ButtonDesigns.designCta}
          text={<Trns source={'New Rule +'} />}
          onClick={(): void => {
            dispatch(
              connectedPush({
                pathname: createAutomationRuleUrl(orgId),
                search: '',
                hash: '',
                state: {},
              }),
            )
          }}
        />
      )}
    </div>
  )
}

export default AutomationRulesListEmptyState
